import {Button, Form, Select, TimePicker} from 'antd';
import dayjs from 'dayjs';

import {PageSubHeader} from '../../../../layout/page-header/page-sub-header';
import {Spinner} from '../../../../layout/spinner/spinner';
import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {Locale} from '../../../../provider/locale/locale';
import {
    localeNameToLanguageNameMap,
    localeNameToShortLocaleNameMap,
    shortLocaleNameToLocaleNameMap,
} from '../../../../provider/locale/locale-context-const';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useUser} from '../../../../provider/user/user-hook';
import {TimezoneType} from '../../../../service/api/api-type';
import {
    useInvitationLink,
    useTelegramAccounts,
    useTelegramAnalyticsSettings,
    useTelegramDirectLink,
    useUpdateTelegramAnalyticsSettings,
} from '../../../../service/notifications-settings/notifications-settings-hook';
import {useProfile} from '../../../../service/profile/profile-hook';
import {handleFilterOption} from '../../../../util/antd/select-helper';
import {classNames} from '../../../../util/css';
import {useProfileTimezone} from '../../profile/personal-information/profile-timezone-hook';
import * as notificationStyles from '../notification.scss';

import {AccountCard} from './account-card/account-card';
import {TelegramAnalyticsFieldsEnum, TelegramAnalyticsFormType} from './telegram-analytics-type';
import * as styles from './telegram-analytics.scss';

// eslint-disable-next-line complexity
export function TelegramAnalytics(): JSX.Element {
    const [form] = Form.useForm<TelegramAnalyticsFormType>();
    const {getLocalizedString} = useLocale();

    const {result: profileResult} = useProfile();
    const {timezoneLibrary, timezoneList} = useProfileTimezone(profileResult);
    const {user} = useUser();

    const {availableLocales} = useDomainConfig();
    const {localeName} = useLocale();

    const {data: telegramActivationLink} = useInvitationLink();
    const {data: telegramDirectLink} = useTelegramDirectLink();
    const {data: telegramAccounts, refetch} = useTelegramAccounts();
    const {data: telegramAnalyticsSettings, isLoading} = useTelegramAnalyticsSettings();
    const {mutateAsync: updateTelegramAnalyticsSettings} = useUpdateTelegramAnalyticsSettings(
        telegramAnalyticsSettings?.id || ''
    );

    async function handleFinishSuccess() {
        const values = form.getFieldsValue();

        if (user) {
            await updateTelegramAnalyticsSettings({
                languageCode:
                    localeNameToShortLocaleNameMap[
                        values[TelegramAnalyticsFieldsEnum.LanguageCode] as keyof typeof localeNameToShortLocaleNameMap
                    ],
                timezone: values[TelegramAnalyticsFieldsEnum.Timezone],
                notificationTime: values[TelegramAnalyticsFieldsEnum.NotificationTime].format('HH:mm:ss'),
            });
        }
    }

    if (isLoading) {
        return <Spinner wrapperHeight={400} />;
    }

    return (
        <Form
            className={notificationStyles.Notification_form}
            form={form}
            initialValues={{
                [TelegramAnalyticsFieldsEnum.Timezone]:
                    telegramAnalyticsSettings?.timezone || profileResult?.profile.timezone,
                [TelegramAnalyticsFieldsEnum.LanguageCode]:
                    (telegramAnalyticsSettings?.languageCode &&
                        shortLocaleNameToLocaleNameMap[
                            telegramAnalyticsSettings?.languageCode as keyof typeof shortLocaleNameToLocaleNameMap
                        ]) ||
                    localeName,
                [TelegramAnalyticsFieldsEnum.NotificationTime]: telegramAnalyticsSettings?.notificationTime
                    ? dayjs(telegramAnalyticsSettings?.notificationTime, 'HH:mm:ss')
                    : dayjs('00:00:00', 'HH:mm:ss'),
            }}
            layout="vertical"
            onFinish={handleFinishSuccess}
        >
            <PageSubHeader className={notificationStyles.Notification_reviewsSubHeader}>
                <Locale stringKey="NOTIFICATIONS__SERVICE_CONNECTION" />
            </PageSubHeader>

            {telegramAccounts?.items.length && telegramAccounts?.items.length > 0 ? (
                <Button href={telegramDirectLink?.link} target="_blank">
                    <Locale stringKey="NOTIFICATIONS__TELEGRAM_ANALYTICS__DIRECT_LINK" />
                </Button>
            ) : (
                <Button href={telegramActivationLink?.link} target="_blank">
                    <Locale stringKey="NOTIFICATIONS__TELEGRAM_ANALYTICS__ACTIVATE_ACCOUNT" />
                </Button>
            )}

            {telegramAccounts?.items && telegramAccounts.items.length > 0 && (
                <div
                    className={classNames(
                        notificationStyles.Notification_reviewsSubHeader__space,
                        styles.TelegramAnalytics_accounts
                    )}
                >
                    {telegramAccounts.items.map((account) => (
                        <AccountCard
                            account={account}
                            activationLink={telegramActivationLink?.link || ''}
                            key={account.id}
                            refetch={refetch}
                        />
                    ))}
                </div>
            )}

            <PageSubHeader
                className={classNames(
                    notificationStyles.Notification_reviewsSubHeader,
                    notificationStyles.Notification_reviewsSubHeader__space
                )}
            >
                <Locale stringKey="NOTIFICATIONS__SETTING_THE_TIME_FOR_RECEIVING_NOTIFICATIONS" />
            </PageSubHeader>

            <Form.Item
                className={notificationStyles.Notification_wideInput}
                label={<Locale stringKey="NOTIFICATIONS__TIME_PERIOD" />}
                name={TelegramAnalyticsFieldsEnum.NotificationTime}
            >
                <TimePicker
                    className={notificationStyles.Notification_wideInput}
                    disabled={!(telegramAccounts?.items.length && telegramAccounts?.items.length > 0)}
                    format="HH:mm"
                    size="large"
                />
            </Form.Item>

            <Form.Item
                className={classNames(
                    notificationStyles.Notification_wideInput,
                    notificationStyles.Notification_reviewsSubHeader__space
                )}
                label={<Locale stringKey="PROFILE__PERSONAL_INFORMATION__LABEL__TIMEZONE" />}
                name={TelegramAnalyticsFieldsEnum.Timezone}
            >
                <Select<string>
                    disabled={
                        timezoneLibrary.isInProgress ||
                        !(telegramAccounts?.items.length && telegramAccounts?.items.length > 0)
                    }
                    filterOption={handleFilterOption}
                    loading={timezoneLibrary.isInProgress}
                    placeholder={getLocalizedString('PROFILE__PERSONAL_INFORMATION__PLACEHOLDER__TIMEZONE')}
                    showSearch
                    size="large"
                >
                    {timezoneList.map((timezoneInList: TimezoneType): JSX.Element => {
                        const {name_for_display: nameForDisplay, name} = timezoneInList;

                        return (
                            <Select.Option key={nameForDisplay} value={name}>
                                {nameForDisplay}
                            </Select.Option>
                        );
                    })}
                </Select>
            </Form.Item>
            <Form.Item
                className={classNames(
                    notificationStyles.Notification_wideInput,
                    notificationStyles.Notification_reviewsSubHeader__space
                )}
                label={<Locale stringKey="NOTIFICATIONS__TELEGRAM_ANALYTICS__LANGUAGE" />}
                name={TelegramAnalyticsFieldsEnum.LanguageCode}
            >
                <Select
                    disabled={!(telegramAccounts?.items.length && telegramAccounts?.items.length > 0)}
                    options={availableLocales.map((availableLocale) => {
                        return {label: localeNameToLanguageNameMap[availableLocale], value: availableLocale};
                    })}
                    size="large"
                    value={localeName}
                />
            </Form.Item>

            <Button
                className={notificationStyles.Notification_reviewsSubHeader__space}
                disabled={!(telegramAccounts?.items.length && telegramAccounts?.items.length > 0)}
                htmlType="submit"
                loading={isLoading}
                type="primary"
            >
                <Locale stringKey="BUTTON__SAVE_SETTINGS" />
            </Button>
        </Form>
    );
}
